import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { pointTime } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { ChevronRightIcon, CredentialIcon, LinkIcon } from '../../shared/ui/icons';
import { ListFooter } from '../../shared/ui/list-footer';
import { Table, type IColumn } from '../../shared/ui/table';
import { EmptyState, ErrorState, LoadingState, NoMatchState } from '../../shared/ui/states';
import {
  ClientStatusMark,
  clientSourceOf,
  clientStatus,
  SOURCE_CUSTOMERS_PAGE,
  sourceClientKeys,
  sourceCustomers,
  type IClientSource,
  type TSourceCustomer,
} from '../../entities/customer';
import type { TSource } from '../../entities/source';
import { AddClient } from './add-client';
import { ClientSearch } from './ui/client-search';

const TILE = 'flex size-6 shrink-0 items-center justify-center rounded-item bg-chip-on text-muted';

const requestsCell = ({ customer }: TSourceCustomer) => {
  const { activity } = customer;
  if (!activity.lastRequestAt) return <span className="text-faint">—</span>;
  if (activity.computedAt === null) return <span className="text-faint">not counted yet</span>;
  return activity.requestsInWindow.toLocaleString('en-US');
};

const columns = (
  source: IClientSource,
  windowHours: number,
  now: number,
  timeZone?: string,
): IColumn<TSourceCustomer>[] => [
  {
    key: 'client',
    header: 'Client',
    width: 'grow',
    ui: true,
    render: ({ customer }) => (
      <span className="flex min-w-0 flex-col">
        <span className="truncate" title={customer.name}>
          {customer.name}
        </span>
        {customer.externalId && (
          <span className="truncate font-mono text-micro text-faint">{customer.externalId}</span>
        )}
      </span>
    ),
  },
  {
    key: 'secret',
    header: 'Secret',
    width: 'w-col-verdict',
    ui: true,
    render: (row) => <ClientStatusMark status={clientStatus(row, source, now, timeZone)} />,
  },
  {
    key: 'requests',
    header: `Requests · ${windowHours} h`,
    width: 'w-col-time-dated',
    align: 'right',
    ui: true,
    render: requestsCell,
  },
  {
    key: 'last',
    header: 'Last request',
    width: 'w-col-time-dated',
    align: 'right',
    ui: true,
    render: ({ customer }) =>
      customer.activity.lastRequestAt ? (
        <span className="font-mono">
          {pointTime(customer.activity.lastRequestAt, now, timeZone)}
        </span>
      ) : (
        <span className="font-mono text-faint">never</span>
      ),
  },
  {
    key: 'open',
    header: '',
    width: 'w-col-flow-icon',
    pinned: true,
    render: () => <ChevronRightIcon size={14} className="text-faint" />,
  },
];

// What arrives without a client's own address: the source's shared one, checked or not.
const GeneralRow = ({ source }: { source: TSource }) => {
  const general = source.verification.general;
  const counted = !source.unauthenticated && source.verification.computedAt !== null;
  return (
    <Link
      to="/sources/$sourceId"
      params={{ sourceId: source.id }}
      className="flex items-center gap-inset rounded-control bg-surface-raised px-input py-inset hover:bg-hover-raised"
    >
      <span className={TILE}>
        <LinkIcon size={12} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-data text-primary">
          Everyone else · the general address of {source.name}
        </span>
        <span className="truncate font-mono text-micro text-faint">
          {source.unauthenticated
            ? 'not checked either'
            : source.clientSecrets
              ? 'checked against the source’s shared secret'
              : 'checked against the same IP allowlist'}
        </span>
      </span>
      {counted && general.checked !== null && (
        <span className="shrink-0 font-mono text-meta text-primary">
          {general.checked.toLocaleString('en-US')} in {source.verification.windowHours} h
        </span>
      )}
    </Link>
  );
};

interface ISourceClientsProps {
  source: TSource;
  search: string;
  onSearch: (next: string) => void;
  onOpenClient: (id: string) => void;
  onSetUpSigning: () => void;
  timeZone?: string;
}

export const SourceClients = ({
  source,
  search,
  onSearch,
  onOpenClient,
  onSetUpSigning,
  timeZone,
}: ISourceClientsProps) => {
  const q = search.trim();

  const clients = useInfiniteQuery({
    queryKey: sourceClientKeys.list(source.id, q),
    queryFn: ({ pageParam }) =>
      sourceCustomers(source.id, {
        ...(q ? { q } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });

  const rows = clients.data?.pages.flatMap((page) => page.data) ?? [];
  const total = clients.data?.pages[0]?.total ?? null;
  const now = useMemo(() => Date.now(), [clients.dataUpdatedAt]);
  const clientSource = clientSourceOf(source);
  const windowHours = rows[0]?.customer.activity.windowHours ?? source.activity.windowHours;
  const left = total === null ? null : total - rows.length;

  const body = () => {
    if (clients.isError) {
      return <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />;
    }
    if (clients.isPending) return <LoadingState rows={5} />;
    if (rows.length === 0) {
      return q ? (
        <NoMatchState onReset={() => onSearch('')} />
      ) : (
        <EmptyState
          title={`No clients on ${source.name} yet`}
          description="Add one when a customer of yours needs their own address. Until then everything arrives on the general address."
          action={
            <AddClient
              source={source}
              variant="secondary"
              onAdded={(added) => onOpenClient(added.customer.id)}
            />
          }
        />
      );
    }
    return (
      <>
        <Table
          tall
          layoutId="source-clients-v2"
          columns={columns(clientSource, windowHours, now, timeZone)}
          rows={rows}
          rowKey={(row) => row.customer.id}
          onRowClick={(row) => onOpenClient(row.customer.id)}
        />
        <ListFooter
          note="a row opens the client’s page"
          shown={rows.length}
          total={total}
          nextLabel={
            left !== null && left > 0 ? `Next ${Math.min(left, SOURCE_CUSTOMERS_PAGE)}` : 'Next'
          }
          onNext={
            clients.hasNextPage && !clients.isFetchingNextPage
              ? () => void clients.fetchNextPage()
              : undefined
          }
        />
      </>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {source.unauthenticated && (
        <div className="flex items-center gap-inset rounded-control border border-border-raised px-input py-3">
          <span className={TILE}>
            <CredentialIcon size={12} />
          </span>
          <p className="min-w-0 flex-1 text-data text-primary">
            {source.name} checks nothing yet, so clients have no secrets. Pick a sender under
            Signing to give each client their own.
          </p>
          <Button size="sm" variant="secondary" onClick={onSetUpSigning}>
            Set up signing
          </Button>
        </div>
      )}
      <div className="flex justify-end">
        <ClientSearch value={search} onChange={onSearch} />
      </div>
      <GeneralRow source={source} />
      {body()}
    </div>
  );
};
